import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { Button } from "@/components/base/buttons/button";
import { DeleteTicketTypeModal } from "@/components/Organisms/DeleteTicketTypeModal/DeleteTicketTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import { authQueryOptions } from "@/services/auth/auth.options";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import {
	useDisableTicketType,
	useDisableTicketTypeSubOrganization,
	useDuplicateTicketType,
	useEnableTicketType,
	useEnableTicketTypeSubOrganization,
	useUpdateTicketType,
} from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import {
	canShowAccessTab,
	canShowActivityTab,
	getTicketTypeDisplayName,
} from "@/utils/ticket-type-detail/ticket-type-detail.utils";
import {
	TicketTypeDrawerContext,
	type TicketTypeDrawerContextValue,
} from "@/utils/ticket-type-detail/ticket-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type TicketTypeTab =
	| "details"
	| "access"
	| "schema"
	| "lifecycle"
	| "rules"
	| "activity";

function getCurrentTab(pathname: string): TicketTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "access" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId",
)({
	component: OrgTicketTypeIdLayout,
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; ticketTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				ticketTypeId: z.string().parse(p.ticketTypeId),
			};
		},
	},
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params, search }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/ticketTypes/${params.ticketTypeId}`,
		);
		if (exactRoot) {
			const parsed = SearchSchema.safeParse(search);
			const s = parsed.success ? parsed.data : SearchSchema.parse({});
			throw redirect({
				to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/details",
				params: {
					orgId: params.orgId,
					ticketTypeId: params.ticketTypeId,
				},
				search: {
					mode: s.mode,
					presentation: s.presentation,
				},
			});
		}
	},
});

function OrgTicketTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId",
	});

	const isFormRoute = /\/configuration\/ticketTypes\/[^/]+\/form/.test(
		pathname,
	);
	const isFullRoute = /\/configuration\/ticketTypes\/[^/]+\/full/.test(
		pathname,
	);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	/** Always scope API calls to the org from the URL so detail payloads classify custom vs system types correctly. */
	const organizationIdForApi = params.orgId;

	const detailQuery = useQuery(
		ticketQueryOptions.ticketTypeDetail(params.ticketTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteTicketType",
			entityType: "TicketType",
			entityId: params.ticketTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditTicketType",
			entityType: "TicketType",
			entityId: params.ticketTypeId,
		}),
	});
	const accessEdit = useTypeAccessEditAllowed(
		"TicketType",
		params.ticketTypeId,
	);

	const subOrgCountQuery = useQuery(
		organizationQueryOptions.subOrganizations.count(organizationIdForApi, {}),
	);
	const hasSubOrganizations = (subOrgCountQuery.data ?? 0) > 0;

	const updateMutation = useUpdateTicketType();
	const duplicateMutation = useDuplicateTicketType();
	const { duplicate: duplicateTicketType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (ticketTypeId) => ({
				ticketTypeId,
				organizationId: organizationIdForApi,
			}),
			onDuplicated: (ticketTypeId) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/details",
					params: { orgId: organizationIdForApi, ticketTypeId },
					search: { mode: "view", presentation: "full" },
				});
			},
			errorMessage: t("admin.ticketTypes.modal.createError"),
		});
	const enableMutation = useEnableTicketType();
	const disableMutation = useDisableTicketType();
	const enableSubOrgMutation = useEnableTicketTypeSubOrganization();
	const disableSubOrgMutation = useDisableTicketTypeSubOrganization();

	const ticketType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		ticketType?.id,
		ticketType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");
	// null = org-wide access (no sub-orgs); [] = none selected; string[] = grant list
	const [draftSubOrgIds, setDraftSubOrgIds] = useState<string[] | null>(null);

	useEffect(() => {
		if (!ticketType?.id) return;

		const isAccessEdit =
			(search.mode ?? "view") === "edit" &&
			getCurrentTab(pathname) === "access";
		const isDetailsEdit = (search.mode ?? "view") === "edit";
		const name = getTicketTypeDisplayName(ticketType);
		const description = ticketType.description ?? "";
		const icon = ticketType.icon ?? "";
		if (!isDetailsEdit || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (!isDetailsEdit || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (!isDetailsEdit || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
		if (isAccessEdit) return;
		const subOrgIds = (ticketType.ticketTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0
				? subOrgIds
				: ticketType.enabled === false
					? []
					: null,
		);
	}, [ticketType, pathname, search.mode]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/org-admin/$orgId/configuration/ticketTypes",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || ticketType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && ticketType !== null;

	const loadedTicketType = ticketType ?? undefined;
	const displayTicketType =
		loadedTicketType && isDetailReady
			? {
					...loadedTicketType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon,
				}
			: null;

	const canEdit = !!loadedTicketType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedTicketType &&
		isDetailReady &&
		canShowActivityTab("organization", loadedTicketType);
	const showAccess = canShowAccessTab("organization");
	const currentTab = getCurrentTab(pathname);

	const isEditing =
		search.mode === "edit" &&
		(currentTab === "access" ? accessEdit.allowed : canEdit);

	useEffect(() => {
		if (isFormRoute || isFullRoute) return;
		if ((search.mode ?? "view") !== "edit") return;
		if (currentTab === "access") {
			if (accessEdit.isPending || accessEdit.allowed) return;
		} else if (!isDetailReady || canEditQuery.isPending || canEdit) {
			return;
		}
		void navigate({
			to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/${currentTab}`,
			search: { mode: "view", presentation: search.presentation },
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		canEdit,
		canEditQuery.isPending,
		currentTab,
		isDetailReady,
		isFormRoute,
		isFullRoute,
		navigate,
		params.orgId,
		params.ticketTypeId,
		search.mode,
		search.presentation,
	]);

	const tabs: { id: TicketTypeTab; label: string }[] =
		loadedTicketType && isDetailReady
			? [
					{ id: "details", label: t("admin.ticketTypes.drawer.tabs.details") },
					...(showAccess
						? [
								{
									id: "access" as const,
									label: t("admin.ticketTypes.drawer.tabs.access"),
								},
							]
						: []),
					{ id: "schema", label: t("admin.ticketTypes.drawer.tabs.form") },
					{
						id: "lifecycle",
						label: t("admin.ticketTypes.drawer.tabs.lifecycle"),
					},
					{ id: "rules", label: t("admin.ticketTypes.drawer.tabs.rules") },
					...(showActivity
						? [
								{
									id: "activity" as const,
									label: t("admin.ticketTypes.drawer.tabs.activity"),
								},
							]
						: []),
				]
			: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!canDeleteQuery.data && isDetailReady && search.mode !== "edit";
	const canDuplicate = isDetailReady && search.mode !== "edit";

	const isSaving =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending ||
		enableSubOrgMutation.isPending ||
		disableSubOrgMutation.isPending;
	const isValid = draftName.trim() !== "";
	const isDirty =
		draftName !== editedName ||
		draftDescription !== editedDescription ||
		draftIcon !== editedIcon;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedTicketType || !isValid || isSaving) return;
		if (currentTab === "access" ? !accessEdit.allowed : !canEdit) return;
		try {
			if (currentTab === "access") {
				if (hasSubOrganizations) {
					const serverIds = (loadedTicketType.ticketTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					const nextIds = draftSubOrgIds ?? [];
					const toEnable = nextIds.filter((id) => !serverIds.includes(id));
					const toDisable = serverIds.filter((id) => !nextIds.includes(id));
					await Promise.all([
						...toEnable.map((subOrgId) =>
							enableSubOrgMutation.mutateAsync({
								typeId: loadedTicketType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
						...toDisable.map((subOrgId) =>
							disableSubOrgMutation.mutateAsync({
								typeId: loadedTicketType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
					]);
				} else {
					if (draftSubOrgIds === null) {
						await enableMutation.mutateAsync({
							ticketTypeId: loadedTicketType.id,
							organizationId: organizationIdForApi,
						});
					} else {
						await disableMutation.mutateAsync({
							ticketTypeId: loadedTicketType.id,
							organizationId: organizationIdForApi,
						});
					}
				}
				showSuccessToast({
					message: t("admin.ticketTypes.drawer.access.toast.saveSuccess"),
				});
			} else {
				const currentSubOrgIds = (
					loadedTicketType.ticketTypeSubOrganizations ?? []
				)
					.map((s) => s.id)
					.filter((id): id is string => id !== null);
				await updateMutation.mutateAsync({
					ticketTypeId: loadedTicketType.id,
					data: {
						name: draftName.trim(),
						description: draftDescription || null,
						icon: draftIcon || null,
						typeId: loadedTicketType.id,
						statusLifecycleId: loadedTicketType.lifecycleId ?? null,
						subOrganizationIds:
							currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
						locationRequired,
					},
					organizationId: organizationIdForApi,
				});
				setEditedName(draftName.trim());
				setEditedDescription(draftDescription);
				setEditedIcon(draftIcon);
				showSuccessToast({
					message: t("admin.ticketTypes.drawer.details.toast.saveSuccess"),
				});
			}
			void navigate({
				to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/${currentTab}`,
				search: { mode: "view", presentation: search.presentation },
			});
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const cancelDraftFn = () => {
		setDraftName(editedName);
		setDraftDescription(editedDescription);
		setDraftIcon(editedIcon);
		const subOrgIds = (loadedTicketType?.ticketTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0
				? subOrgIds
				: loadedTicketType?.enabled === false
					? []
					: null,
		);
		void navigate({
			to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/${currentTab}`,
			search: { mode: "view", presentation: search.presentation },
		});
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedTicketType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		const currentSubOrgIds = (loadedTicketType.ticketTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			ticketTypeId: loadedTicketType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedTicketType.id,
				statusLifecycleId: loadedTicketType.lifecycleId ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired,
			},
			organizationId: organizationIdForApi,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedTicketType) return;
		const currentSubOrgIds = (loadedTicketType.ticketTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			ticketTypeId: loadedTicketType.id,
			data: {
				name: (editedName || loadedTicketType.name || "").trim(),
				description: editedDescription || loadedTicketType.description || null,
				icon: editedIcon || loadedTicketType.icon || null,
				typeId: loadedTicketType.id,
				statusLifecycleId: loadedTicketType.lifecycleId ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired: next,
			},
			organizationId: organizationIdForApi,
		});
		setLocationRequired(next);
	};

	const contextValue: TicketTypeDrawerContextValue | null =
		loadedTicketType && isDetailReady && displayTicketType
			? {
					scope: "organization",
					ticketType: displayTicketType,
					mode: search.mode,
					setMode: (mode) => {
						void navigate({
							to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/${currentTab}`,
							search: { mode, presentation: search.presentation },
						});
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds,
					setDraftSubOrgIds,
					isSaving,
					isValid,
					isDirty,
					saveDraft: saveDraftFn,
					cancelDraft: cancelDraftFn,
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayTicketType !== null
			? getTicketTypeDisplayName(displayTicketType)
			: t("admin.ticketTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedTicketType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedTicketType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedTicketType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedTicketType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	const editFooter =
		isEditing && currentTab !== "access" && contextValue ? (
			<div className="flex w-full items-center justify-end gap-3">
				<Button
					color="secondary"
					size="md"
					onClick={contextValue.cancelDraft}
					isDisabled={isSaving}
				>
					{t("admin.ticketTypes.drawer.actions.cancel")}
				</Button>
				<Button
					color="primary"
					size="md"
					data-testid="ticket-type-drawer-save-btn"
					onClick={() => void contextValue.saveDraft()}
					isDisabled={!isValid || isSaving}
					isLoading={isSaving}
				>
					{t("admin.ticketTypes.drawer.actions.save")}
				</Button>
			</div>
		) : undefined;

	if (isFullRoute) {
		return contextValue ? (
			<TicketTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</TicketTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	// useRouterState updates before the component unmounts, so during navigation
	// away from any $ticketTypeId route the pathname briefly shows the new URL.
	// Guard against mounting the Drawer during that window.
	const isOnTicketTypeSubroute = /\/configuration\/ticketTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnTicketTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="ticket"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/full`,
						search: { tab: "details", mode: "view" },
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.ticketTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateTicketType(params.ticketTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.ticketTypes.drawer.footer.deleteTicketType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.ticketTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.ticketTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/ticketTypes/${params.ticketTypeId}/${tabId}`,
						search: {
							mode: search.mode,
							presentation: search.presentation,
						},
					})
				}
				footer={editFooter}
				data-testid="ticket-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<TicketTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</TicketTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteTicketTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={() => {
					void navigate({
						to: "/org-admin/$orgId/configuration/ticketTypes",
						params: { orgId: params.orgId },
					});
				}}
				ticketTypeId={params.ticketTypeId}
				ticketTypeName={ticketType ? getTicketTypeDisplayName(ticketType) : ""}
				organizationId={organizationIdForApi}
				scope="organization"
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.ticketTypes.duplicate.inProgress")}
				testId="tickettype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
